if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.onVariantInputChange = this.onVariantInputChange.bind(this);
        this.onLuxBuyNowClick = this.onLuxBuyNowClick.bind(this);
        this.initializeLuxBuyButtons();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        if (!this.isLuxBuyButtons) return;

        this.variantIdInput?.addEventListener('change', this.onVariantInputChange);
        this.buyNowButton?.addEventListener('click', this.onLuxBuyNowClick);
        this.syncLuxButtons();
      }

      disconnectedCallback() {
        if (!this.isLuxBuyButtons) return;

        this.variantIdInput?.removeEventListener('change', this.onVariantInputChange);
        this.buyNowButton?.removeEventListener('click', this.onLuxBuyNowClick);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const bundleAddons = this.getSelectedBundleAddons();
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (bundleAddons.length) {
          const quantity = Number(formData.get('quantity') || 1);
          const parentVariantId = Number(formData.get('id'));
          const bundleKey = this.createLuxBundleKey();
          const items = [
            {
              id: parentVariantId,
              quantity,
              properties: this.createLuxBundleProperties('main', bundleKey, parentVariantId, bundleAddons),
            },
            ...bundleAddons.map((variantId) => ({
              id: Number(variantId),
              quantity,
              properties: this.createLuxBundleProperties('addon', bundleKey, parentVariantId),
            })),
          ];

          config.body = JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          });
        } else {
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getSelectedBundleInput() {
        const sectionId = this.dataset.sectionId;
        const variantSelects = sectionId ? document.getElementById(`variant-selects-${sectionId}`) : null;
        return variantSelects?.querySelector(
          '.product-form__input--quantity-breaks input[type="radio"]:checked[data-bundle-addons]'
        );
      }

      getSelectedBundleAddons() {
        const selectedBundle = this.getSelectedBundleInput();

        if (!selectedBundle?.dataset.bundleAddons) return [];

        return selectedBundle.dataset.bundleAddons
          .split(',')
          .map((variantId) => variantId.trim())
          .filter(Boolean);
      }

      getSelectedBundleTotalPrice() {
        const selectedBundle = this.getSelectedBundleInput();
        const totalPrice = Number(selectedBundle?.dataset.bundleTotalPrice || 0);

        return Number.isFinite(totalPrice) && totalPrice > 0 ? totalPrice : null;
      }

      createLuxBundleKey() {
        return `lux-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      createLuxBundleProperties(role, bundleKey, parentVariantId, addonVariantIds = []) {
        return {
          _lux_bundle_key: bundleKey,
          _lux_bundle_role: role,
          _lux_bundle_parent: String(parentVariantId || ''),
          _lux_bundle_required_addons: addonVariantIds.join(','),
        };
      }

      initializeLuxBuyButtons() {
        this.isLuxBuyButtons = this.classList.contains('lux-buy-buttons');
        if (!this.isLuxBuyButtons) return;

        this.variants = this.parseLuxVariants();
        this.priceEl = this.querySelector('[data-lux-atc-price]');
        this.labelEl = this.querySelector('.lux-atc-btn__text');
        this.buyNowButton = this.querySelector('[data-lux-buy-now]');
        this.buyNowButtonDefaultText = this.buyNowButton?.textContent?.trim() || '';
        this.moneyFormat = this.dataset.moneyFormat;
      }

      parseLuxVariants() {
        const variantsDataEl = this.querySelector('[data-lux-variants]');
        if (!variantsDataEl) return [];

        try {
          return JSON.parse(variantsDataEl.textContent);
        } catch (error) {
          console.error('Lux buy buttons variants parse error', error);
          return [];
        }
      }

      formatLuxMoney(cents) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, this.moneyFormat);
        }

        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }

      findLuxVariant(variantId) {
        return this.variants.find((variant) => String(variant.id) === String(variantId));
      }

      getCurrentLuxVariant() {
        return this.findLuxVariant(this.variantIdInput?.value);
      }

      syncLuxButtons() {
        if (!this.isLuxBuyButtons || !this.submitButton || !this.labelEl || !this.priceEl) return;

        const variant = this.getCurrentLuxVariant();

        if (!variant) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.buyNowButton?.setAttribute('disabled', 'disabled');

          if (!this.submitButton.classList.contains('loading')) {
            this.labelEl.textContent = window.variantStrings?.unavailable || 'Unavailable';
          }
          return;
        }

        this.priceEl.textContent = this.formatLuxMoney(this.getSelectedBundleTotalPrice() || variant.price);

        if (variant.available) {
          this.submitButton.removeAttribute('disabled');
          this.buyNowButton?.removeAttribute('disabled');

          if (!this.submitButton.classList.contains('loading')) {
            this.labelEl.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.buyNowButton?.setAttribute('disabled', 'disabled');

          if (!this.submitButton.classList.contains('loading')) {
            this.labelEl.textContent = window.variantStrings?.soldOut || 'Sold out';
          }
        }
      }

      onVariantInputChange() {
        this.syncLuxButtons();
      }

      async onLuxBuyNowClick() {
        const variant = this.getCurrentLuxVariant();

        if (!variant || !variant.available || this.buyNowButton?.dataset.processing === 'true') return;

        this.buyNowButton.dataset.processing = 'true';
        this.buyNowButton.classList.add('is-loading');
        this.buyNowButton.textContent = 'Redirecting...';

        try {
          const bundleAddons = this.getSelectedBundleAddons();
          const bundleKey = this.createLuxBundleKey();
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(
              bundleAddons.length
                ? {
                    items: [
                      {
                        id: variant.id,
                        quantity: 1,
                        properties: this.createLuxBundleProperties('main', bundleKey, variant.id, bundleAddons),
                      },
                      ...bundleAddons.map((variantId) => ({
                        id: Number(variantId),
                        quantity: 1,
                        properties: this.createLuxBundleProperties('addon', bundleKey, variant.id),
                      })),
                    ],
                  }
                : {
                    id: variant.id,
                    quantity: 1,
                  }
            ),
          });

          if (!response.ok) {
            throw new Error('Could not continue to checkout');
          }

          window.location.href = '/checkout';
        } catch (error) {
          console.error(error);
          this.buyNowButton.textContent = this.buyNowButtonDefaultText;
          this.buyNowButton.classList.remove('is-loading');
          this.buyNowButton.dataset.processing = 'false';
        }
      }
    }
  );
}
